import React, { useState } from 'react';
import { useUID } from 'react-uid';
import { useFormik } from 'formik';
import Flex from 'toolbox/src/components/Flex/Flex';

interface InlineTextEditProps {
  setWhichInputIdIsBeingEdited: (id: string | null) => void;
  whichInputIdIsBeingEdited: string | null;
  onSubmit: (params: { value: string }) => void;
  value: string | number;
  label: string;
  isLoading: boolean;
}

type State = 'isBeingEdited' | 'somethingElseIsBeingEdited' | 'idle';

export const InlineTextEdit: React.FC<InlineTextEditProps> = ({
  whichInputIdIsBeingEdited,
  setWhichInputIdIsBeingEdited,
  value,
  isLoading,
  onSubmit,
  label,
  children,
}) => {
  const id = useUID();

  let state: State = 'idle';

  if (whichInputIdIsBeingEdited === id) {
    state = 'isBeingEdited';
  } else if (whichInputIdIsBeingEdited) {
    state = 'somethingElseIsBeingEdited';
  }

  if (state === 'isBeingEdited') {
    return (
      <Form
        label={label}
        onSubmit={onSubmit}
        initialValue={value}
        isLoading={isLoading}
        onReset={() => setWhichInputIdIsBeingEdited(null)}
      />
    );
  }

  return (
    <button
      disabled={state === 'somethingElseIsBeingEdited'}
      onClick={() => {
        if (state === 'idle') {
          setWhichInputIdIsBeingEdited(id);
        }
      }}
      className="w-24 h-8 text-left focus:bg-gray-200 focus:outline-none"
    >
      {children}
    </button>
  );
};

export default InlineTextEdit;

const Form = ({
  initialValue,
  onSubmit,
  isLoading,
  label,
  onReset,
}: {
  label: string;
  initialValue: string | number;
  isLoading: boolean;
  onReset: () => void;
  onSubmit: ({ value }) => void;
}) => {
  const { values, handleChange, handleSubmit } = useFormik<{
    value: string | number;
  }>({
    initialValues: {
      value: initialValue,
    },
    onSubmit: (values) => {
      if (!isLoading) {
        onSubmit(values);
      }
    },
  });
  return (
    <form onSubmit={handleSubmit}>
      <Flex mx={0} alignItems="center" className="inline-flex h-8">
        <input
          type="text"
          name="value"
          className="inline w-12 min-w-0"
          autoFocus
          onChange={handleChange}
          value={values.value}
          aria-label={label}
        />
        <button
          className="px-2 py-1 text-xs text-gray-700 uppercase bg-gray-100 focus:bg-gray-200"
          type="button"
          onClick={onReset}
        >
          Reset
        </button>
        <button
          className="px-2 py-1 text-xs uppercase bg-jcap-100 text-jcap-700 focus:bg-jcap-200"
          type="submit"
        >
          Save
        </button>
      </Flex>
    </form>
  );
};

export const useInlineTextEditState = () => {
  const [whichInputIdIsBeingEdited, setWhichInputIdIsBeingEdited] = useState<
    string | null
  >(null);

  return {
    whichInputIdIsBeingEdited,
    setWhichInputIdIsBeingEdited,
    closeAllInputs: () => setWhichInputIdIsBeingEdited(null),
  };
};
